import type { QueryView } from '@krp/client-runtime'
import type { ChoiceChip, SelectOption } from '@krp/ui-kit'

import type { CashReceiptsNavVm } from '../../receipts/model/types'

/** Состояние смены: открыта, закрыта или по данным 1С не понять (`null`). */
export type CashShiftState = 'open' | 'closed'

/**
 * Кассовая смена 1С (`Document_КассоваяСмена`). Реквизиты смены с живой базой не сверены: поле, которого база не отдаёт, —
 * `null` («не знаем», а не ноль). Даты — местное время базы без пояса: `2026-10-07T09:00:00`.
 */
export interface CashShift {
  key: string
  number: string | null
  date: string | null
  posted: boolean
  registerKey: string | null
  registerName: string | null
  shiftNumber: string | null
  openedAt: string | null
  closedAt: string | null
  state: CashShiftState | null
  /** Статус смены 1С дословно. */
  status: string | null
  cashierKey: string | null
  cashierName: string | null
  /** Итоги смены служба пока не считает — `null`. */
  receiptCount: number | null
  income: number | null
  refunds: number | null
}

export interface CashShifts {
  items: readonly CashShift[]
  from: string
  to: string
  truncated: boolean
  observedAt: string
}

export interface CashShiftsArgs {
  resourceId: string
  from: string
  to: string
}

/** Отбор состояния: пусто — все смены. */
export type CashShiftStateFilter = '' | CashShiftState

/**
 * Намерение экрана «Смены 1С» по базе: место дерева (`''` — вся база), месяц (`ГГГГ-ММ-01`, `null` — текущий), день внутри
 * месяца (`null` — весь месяц), кассир (пусто — все), состояние и свёрнутые узлы дерева.
 */
export interface CashShiftsViewState {
  nodeId: string
  month: string | null
  day: string | null
  cashier: string
  state: CashShiftStateFilter
  collapsed: string[]
}

export type CashShiftsIntentState = Record<string, CashShiftsViewState>

export const EMPTY_SHIFTS_VIEW: CashShiftsViewState = { nodeId: '', month: null, day: null, cashier: '', state: '', collapsed: [] }

/** День месяца в полосе дней: сколько смен по отбору; число — текстом, а не только цветом. */
export interface CashShiftDayVm {
  day: string
  /** «7» — число месяца. */
  date: string
  weekday: string
  count: number
  selected: boolean
  today: boolean
  future: boolean
  label: string
}

export interface CashShiftRowVm {
  id: string
  title: string
  register: string
  cashier: string
  opened: string
  closed: string
  state: CashShiftState | null
  /** «Открыта» / «Закрыта» / «Не понять» — словом, рядом со значком. */
  stateLabel: string
  /** Статус 1С дословно и номер документа. */
  note: string | null
}

export interface CashShiftsScreenVm {
  nav: CashReceiptsNavVm
  month: string
  monthTitle: string
  previousMonth: string
  nextMonth: string
  /** Следующий месяц уже наступил — стрелка вперёд доступна. */
  canNext: boolean
  days: readonly CashShiftDayVm[]
  day: string | null
  states: readonly ChoiceChip[]
  state: CashShiftStateFilter
  cashiers: readonly SelectOption[]
  cashier: string
  query: QueryView
  /** «Смен: 12» по отбору. */
  countText: string
  readAt: string | null
  truncatedNote: string | null
  filteredOut: boolean
  rows: readonly CashShiftRowVm[]
}

export interface CashShiftsScreenActions {
  nodeChosen: (nodeId: string) => void
  nodeToggled: (nodeId: string) => void
  monthChanged: (month: string) => void
  dayChanged: (day: string | null) => void
  cashierChanged: (cashier: string) => void
  stateChanged: (state: CashShiftStateFilter) => void
  /** «Обновить»: перечитать кассы и смены из 1С. */
  refresh: () => void
  retry: () => void
}
